import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useAppSelector } from '@app/store/hooks';
import { selectUser } from '@features/auth';
import { paths } from '@shared/config/paths';
import { Icon } from '@shared/ui/icon/Icon';

export interface HeaderProps {
  /** État replié de la sidebar (desktop). */
  collapsed: boolean;
  /** Replie/déplie la sidebar (desktop). */
  onToggleSidebar: () => void;
  /** Ouvre le tiroir latéral (mobile / tablette). */
  onOpenSidebar: () => void;
}

/** Salutation selon l'heure de la journée. */
function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Bonjour';
  if (hour < 18) return 'Bon après-midi';
  return 'Bonsoir';
}

/**
 * En-tête pleine largeur : bouton menu, salutation, recherche, notifications,
 * profil utilisateur. Esthétique FinTech premium.
 */
export function Header({ collapsed, onToggleSidebar, onOpenSidebar }: HeaderProps) {
  const user = useAppSelector(selectUser);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const firstName = user?.name.split(' ')[0] ?? 'là';

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    if (query.trim()) {
      void navigate(`${paths.receipts}?q=${encodeURIComponent(query.trim())}`);
    }
  };

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-4 border-b border-slate-200 bg-white px-4 sm:px-6">
      {/* Bouton menu — sandwich sur mobile, repli sur desktop */}
      <button
        type="button"
        aria-label="Ouvrir le menu"
        onClick={onOpenSidebar}
        className="flex size-9 shrink-0 items-center justify-center rounded-md text-slate-600 transition-colors hover:bg-slate-100 lg:hidden"
      >
        <Icon name="menu" size={20} />
      </button>
      <button
        type="button"
        onClick={onToggleSidebar}
        aria-label={collapsed ? 'Déplier la navigation' : 'Replier la navigation'}
        aria-expanded={!collapsed}
        title={collapsed ? 'Déplier la navigation' : 'Replier la navigation'}
        className="hidden size-9 shrink-0 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 lg:flex"
      >
        <Icon name={collapsed ? 'panel-right' : 'panel-left'} size={18} />
      </button>

      {/* Logo (visible quand la sidebar est repliée) */}

      {/* Salutation */}
      <div className="hidden min-w-0 flex-col sm:flex">
        <h1 className="truncate text-[15px] font-semibold text-slate-900">
          {greeting()}, {firstName}
        </h1>
        <p className="truncate text-xs text-slate-500">
          Voici l'aperçu de vos dépenses aujourd'hui.
        </p>
      </div>

      {/* Recherche */}
      <form
        onSubmit={handleSearch}
        role="search"
        className="relative mx-auto hidden w-full max-w-md md:block"
      >
        <Icon
          name="search"
          size={16}
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-slate-400"
        />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Rechercher un ticket, un commerçant…"
          aria-label="Rechercher"
          className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 pr-3 pl-9 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:bg-white focus:outline-2 focus:outline-emerald-600"
        />
      </form>

      {/* Notifications + profil */}
      <div className="ml-auto flex items-center gap-2">
        <button
          type="button"
          aria-label="Notifications"
          className="relative flex size-9 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <Icon name="bell" size={18} />
          <span
            aria-hidden="true"
            className="absolute top-1.5 right-1.5 size-2 rounded-full bg-emerald-500"
          />
        </button>
      </div>
    </header>
  );
}
